/**
 * Accept Organization Invitation Page
 *
 * Landing page for users clicking invitation links from email.
 * Flow:
 * 1. User lands here from email link with ?token=xxx
 * 2. Page fetches invitation details (name, email, org)
 * 3. User creates a password
 * 4. Account is created and user is signed in automatically
 * 5. Redirects to compliance dashboard
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  Building2,
  Eye,
  EyeOff,
  Mail,
  User,
} from 'lucide-react';

interface InvitationDetails {
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: string;
  orgName: string;
  expiresAt: string | null;
}

export default function AcceptInvite() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const inviteToken = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'ready' | 'creating' | 'success' | 'error' | 'existing_user'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [successData, setSuccessData] = useState<{ orgName: string; role: string } | null>(null);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // For existing users who need to login
  const [existingPassword, setExistingPassword] = useState('');

  // Fetch invitation details on mount
  useEffect(() => {
    if (!inviteToken) {
      setStatus('error');
      setErrorMessage('No invitation token provided. Please check your invitation link.');
      return;
    }

    fetchInvitationDetails();
  }, [inviteToken]);

  // If user is already logged in and we have invitation details, try to accept
  useEffect(() => {
    if (user && invitation && status === 'ready') {
      // User is already logged in - check if email matches
      if (user.email?.toLowerCase() === invitation.email.toLowerCase()) {
        acceptWithExistingAccount();
      } else {
        setStatus('error');
        setErrorMessage(`This invitation was sent to ${invitation.email}. You're signed in as ${user.email}. Please sign out first.`);
      }
    }
  }, [user, invitation, status]);

  const fetchInvitationDetails = async () => {
    try {
      const getInvitationDetails = httpsCallable(functions, 'getInvitationDetails');
      const result = await getInvitationDetails({ inviteToken });
      const data = result.data as InvitationDetails & { success: boolean };

      if (data.success) {
        setInvitation(data);
        setStatus('ready');
      }
    } catch (error: any) {
      console.error('Failed to fetch invitation:', error);
      setStatus('error');

      let message = 'Failed to load invitation. Please try again.';
      if (error.message?.includes('not-found')) {
        message = 'This invitation is invalid or has already been used.';
      } else if (error.message?.includes('deadline-exceeded')) {
        message = 'This invitation has expired. Please contact your administrator for a new one.';
      } else if (error.message?.includes('failed-precondition')) {
        message = 'This organization has been suspended. Please contact support.';
      }

      setErrorMessage(message);
    }
  };

  const acceptWithExistingAccount = async () => {
    if (!inviteToken) return;

    setStatus('creating');

    try {
      const acceptOrgInvitation = httpsCallable(functions, 'acceptOrgInvitation');
      const result = await acceptOrgInvitation({ inviteToken });
      const data = result.data as {
        success: boolean;
        orgId: string;
        orgName: string;
        role: string;
        message: string;
      };

      if (data.success) {
        setSuccessData({ orgName: data.orgName, role: data.role });
        setStatus('success');

        toast({
          title: 'Welcome!',
          description: data.message,
        });

        setTimeout(() => {
          navigate('/compliance');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Failed to accept invitation:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Failed to accept invitation.');
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your passwords match.',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      });
      return;
    }

    setFormLoading(true);

    try {
      const createAccountAndAcceptInvitation = httpsCallable(functions, 'createAccountAndAcceptInvitation');
      const result = await createAccountAndAcceptInvitation({
        inviteToken,
        password,
      });

      const data = result.data as {
        success: boolean;
        orgId: string;
        orgName: string;
        role: string;
        message: string;
      };

      if (data.success) {
        setSuccessData({ orgName: data.orgName, role: data.role });
        setStatus('success');

        toast({
          title: 'Account Created!',
          description: 'Please sign in to continue.',
        });

        // Redirect to auth page to sign in
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Failed to create account:', error);

      if (error.message?.includes('already-exists')) {
        // User already has an account - redirect to auth
        toast({
          title: 'Account exists',
          description: 'Please sign in with your existing password.',
        });
        setTimeout(() => {
          navigate('/auth');
        }, 1500);
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to create account. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleExistingUserLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invitation) return;

    setFormLoading(true);

    try {
      // Sign in with existing credentials
      await signInWithEmailAndPassword(auth, invitation.email, existingPassword);

      toast({
        title: 'Signed in',
        description: 'Accepting your invitation...',
      });

      // The useEffect will handle accepting once user state updates
    } catch (error: any) {
      console.error('Login error:', error);

      let message = 'Invalid password. Please try again.';
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        message = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many attempts. Please try again later.';
      }

      toast({
        title: 'Login failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setFormLoading(false);
    }
  };

  // Loading state
  if (authLoading || status === 'loading') {
    return (
      <div className="min-h-screen bg-[hsl(220,25%,6%)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(220,70%,50%)] mx-auto mb-4" />
          <p className="text-gray-400">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[hsl(220,25%,6%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[hsl(220,22%,10%)] border-[hsl(220,15%,18%)]">
          <CardHeader className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <CardTitle className="text-xl text-white">Invitation Error</CardTitle>
            <CardDescription className="text-gray-400">{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/')}
              className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === 'success' && successData) {
    return (
      <div className="min-h-screen bg-[hsl(220,25%,6%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[hsl(220,22%,10%)] border-[hsl(220,15%,18%)]">
          <CardHeader className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <CardTitle className="text-xl text-white">Welcome to {successData.orgName}!</CardTitle>
            <CardDescription className="text-gray-400">
              You've joined as {successData.role}. Redirecting to dashboard...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[hsl(220,70%,50%)]" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Creating state
  if (status === 'creating') {
    return (
      <div className="min-h-screen bg-[hsl(220,25%,6%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[hsl(220,22%,10%)] border-[hsl(220,15%,18%)]">
          <CardHeader className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[hsl(220,70%,50%)] mx-auto mb-4" />
            <CardTitle className="text-xl text-white">Setting Up Your Account</CardTitle>
            <CardDescription className="text-gray-400">
              Please wait while we create your account...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Existing user login form
  if (status === 'existing_user' && invitation) {
    return (
      <div className="min-h-screen bg-[hsl(220,25%,6%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[hsl(220,22%,10%)] border-[hsl(220,15%,18%)]">
          <CardHeader className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-[hsl(220,70%,50%)]/20 flex items-center justify-center mb-4">
              <Building2 className="h-6 w-6 text-[hsl(220,70%,60%)]" />
            </div>
            <CardTitle className="text-xl text-white">Welcome Back!</CardTitle>
            <CardDescription className="text-gray-400">
              Sign in to join <span className="text-white font-medium">{invitation.orgName}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleExistingUserLogin} className="space-y-4">
              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <div className="flex items-center gap-2 px-3 py-2 bg-[hsl(220,20%,12%)] border border-[hsl(220,15%,25%)] rounded-md">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-300">{invitation.email}</span>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="existingPassword" className="text-gray-300">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="existingPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={existingPassword}
                    onChange={(e) => setExistingPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={formLoading}
                className="w-full bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)] text-white"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In & Join'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ready state - show password creation form
  if (!invitation) return null;

  return (
    <div className="min-h-screen bg-[hsl(220,25%,6%)] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[hsl(220,22%,10%)] border-[hsl(220,15%,18%)]">
        <CardHeader className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-[hsl(220,70%,50%)]/20 flex items-center justify-center mb-4">
            <Building2 className="h-6 w-6 text-[hsl(220,70%,60%)]" />
          </div>
          <CardTitle className="text-xl text-white">
            Welcome{invitation.firstName ? `, ${invitation.firstName}` : ''}!
          </CardTitle>
          <CardDescription className="text-gray-400">
            Create your password to join <span className="text-white font-medium">{invitation.orgName}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateAccount} className="space-y-4">
            {/* User info (read-only) */}
            <div className="space-y-3 p-3 bg-[hsl(220,20%,12%)] rounded-lg border border-[hsl(220,15%,20%)]">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-gray-300 text-sm">
                  {invitation.firstName} {invitation.lastName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-gray-300 text-sm">{invitation.email}</span>
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">
                Create Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-300">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                minLength={6}
                className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
              />
            </div>

            <Button
              type="submit"
              disabled={formLoading}
              className="w-full bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)] text-white"
            >
              {formLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account & Join'
              )}
            </Button>
          </form>

          <p className="mt-4 text-center text-xs text-gray-500">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
